// Couche de données des inventaires terrain : les écrans n'appellent que ce fichier.
// Tout passe par le serveur (inventaires, tours, interventions, photos). Restent simulés les réglages
// « Nous faisons des réassorts en saison » (restockInSeason) et « Tour avec inventaire »
// (tourWithInventory). Le journal de Mon activité et la vue Plan / Liste du tour restent gardés sur le téléphone.
//
// Démo : ajouter ?demo=no-restock à l'adresse pour un camping qui ne fait pas de réassort en saison,
// ?demo=inspection pour un tour sans inventaire.

import connexion from '../../../connexion'
import { normalizeLocation } from './fieldSearch'
import { mergePending, hasPending, enqueueTourEntries, isNetworkError } from './fieldOutbox'

function demoFlags()
{
	if (typeof window === 'undefined')
		return []
	const params = new URLSearchParams(window.location.search)
	return (params.get('demo') || '').split(',').map(flag => flag.trim())
}

function isOffline()
{
	return typeof navigator !== 'undefined' && navigator.onLine === false
}

function getJson(path, options)
{
	return fetch(connexion.connect.url + path, options)
		.then(response => response.json())
}

function postJson(path, body, signal)
{
	return fetch(connexion.connect.url + path, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(body),
		signal
	})
		.then(response => response.json())
}

// Le serveur répond false quand l'enregistrement a échoué
function requireDocument(result, label)
{
	if (!result || !result._id)
		throw new Error(label)
	return result
}

// Envoi d'une photo sur /dashboard/files/add-image ; renvoie le fichier enregistré ({ _id, ... })
function uploadImage(file, directory, signal)
{
	const body = new FormData()
	body.append('file', file)
	body.append('path', directory)
	return fetch(connexion.connect.url + 'dashboard/files/add-image', { method: 'POST', body, signal })
		.then(response => response.json())
		.then(saved => requireDocument(saved, 'upload'))
}

// Au plus UPLOAD_PARALLEL envois de photos à la fois : le téléphone (réseau mobile) et le serveur ne sont pas
// saturés par une dizaine de photos en même temps
const UPLOAD_PARALLEL = 2

function mapLimit(list, limit, task)
{
	const results = new Array(list.length)
	let next = 0
	const worker = () => {
		if (next >= list.length)
			return Promise.resolve()
		const index = next
		next++
		return Promise.resolve(task(list[index], index)).then(result => {
			results[index] = result
			return worker()
		})
	}
	return Promise.all(Array.from({ length: Math.min(limit, list.length) }, worker)).then(() => results)
}

// Une photo déjà envoyée n'est pas renvoyée si l'enregistrement est retenté après un échec
const uploadedFiles = new WeakMap()

function uploadOnce(file, directory, signal)
{
	if (uploadedFiles.has(file))
		return Promise.resolve(uploadedFiles.get(file))
	return uploadImage(file, directory, signal).then(saved => {
		uploadedFiles.set(file, saved)
		return saved
	})
}

// Tours de terrain (collection `tours`). Seuls les administrateurs (droit admin à 7) en créent,
// les terminent ou les rouvrent : le serveur le vérifie avec l'identifiant de l'utilisateur.

// Une photo du tour envoyée au serveur n'a plus que file_id : ses adresses d'affichage s'en déduisent.
// Le serveur garde la photo pleine taille et des miniatures de 720 et 360 px : 360 pour les grilles (thumb),
// 720 pour les cartes et pages entières (url). Sans taille, c'est la pleine taille (adresse jointe aux interventions).
export function photoUrl(fileId, size)
{
	return connexion.connect.url + 'dashboard/files/get-image/' + fileId + (size ? '/' + size : '')
}

// Les actions encore en file d'attente sur ce téléphone (enregistrées sans réseau) s'ajoutent à ce que renvoie le serveur
function hydrateTour(tour)
{
	return mergePending({
		...tour,
		entries: (tour.entries || []).map(entry => (entry.photo && entry.photo.file_id ? { ...entry, photo: { ...entry.photo, url: photoUrl(entry.photo.file_id, 720), thumb: photoUrl(entry.photo.file_id, 360) } } : entry))
	}, photoUrl)
}

function tourRequest(path, body)
{
	if (isOffline())
		return Promise.reject(new Error('offline'))
	return postJson(path, body).then(tour => hydrateTour(requireDocument(tour, 'tour')))
}

// Les tours sont lus réseau d'abord : cache 'no-cache' pour ne jamais servir une copie du navigateur, et le service
// worker (NetworkFirst sur les GET) ne rend sa copie que hors ligne. Le tour change à chaque geste des collègues.
const FRESH = { cache: 'no-cache' }

export function fetchTours()
{
	return getJson('dashboard/tours/list', FRESH)
		.then(list => {
			if (!Array.isArray(list))
				throw new Error('tours')
			return list.map(hydrateTour)
		})
}

// Le tour ouvert le plus récent (null sans tour, ou si le serveur ne répond pas)
export function fetchOpenTour()
{
	return fetchTours()
		.then(list => {
			const open = list.filter(tour => tour.status === 'open')
			open.sort((a, b) => (b.last_activity_time || 0) - (a.last_activity_time || 0))
			return open[0] || null
		})
		.catch(() => null)
}

export function fetchTour(id)
{
	return getJson('dashboard/tours/find?id=' + encodeURIComponent(id), FRESH)
		.then(tour => (tour && tour._id ? hydrateTour(tour) : null))
		.catch(() => null)
}

export function createTour(name, user)
{
	return tourRequest('dashboard/tours/create', { name, user: user ? user._id : null })
}

export function closeTour(tour, user)
{
	return tourRequest('dashboard/tours/close', { id: tour._id, user: user ? user._id : null })
}

export function reopenTour(tour, user)
{
	return tourRequest('dashboard/tours/reopen', { id: tour._id, user: user ? user._id : null })
}

export function hideTourFor(tour, user)
{
	return tourRequest('dashboard/tours/hide', { id: tour._id, user: user ? user._id : null })
}

// Réaffiche sur mon accueil un tour que j'avais masqué
export function showTourFor(tour, user)
{
	return tourRequest('dashboard/tours/unhide', { id: tour._id, user: user ? user._id : null })
}

export function keepTourOpen(tour, user)
{
	return tourRequest('dashboard/tours/keep-open', { id: tour._id, user: user ? user._id : null })
}

// Actions du tour (inventaire, rien à signaler, commentaire, photo, intervention) : on envoie ce qui diffère
// entre l'état précédent et le suivant, le serveur l'ajoute aux actions des collègues. Les photos partent
// d'abord sur /dashboard/files/add-image. Renvoie le tour à jour.
// Sans réseau (hors ligne, délai dépassé), l'enregistrement est mis en file d'attente sur le téléphone (fieldOutbox) et
// l'écran garde ce qui vient d'être saisi : le service worker de synchronisation l'envoie dès que le réseau revient.
// Tant qu'une file existe pour ce tour, les changements suivants s'y rangent aussi, pour partir dans l'ordre.
// Une action « intervention » portant pending_intervention { doc, photos } : l'intervention est créée d'abord (doc.client_ref
// évite un doublon si la création est rejouée), puis l'action part avec son intervention_id.
// Une action « inventaire » portant pending_inventory { payload } : l'inventaire du tour est enregistré d'abord
// (insert-staff modifie l'inventaire déjà enregistré pour ce tour et cet emplacement : un rejeu ne le double pas), puis
// l'action part avec son inventory_id. Une intervention signalée pendant cet inventaire porte pending_intervention.after_inventory
// (identifiant de l'action inventaire) : elle est créée après lui, avec son inventory_id.
const SAVE_TIMEOUT_MS = 20 * 1000 // délai de base d'un enregistrement, plus PHOTO_TIMEOUT_MS par photo à envoyer
const PHOTO_TIMEOUT_MS = 10 * 1000

// Inventaires et interventions à créer avant l'action du tour, une par une et dans l'ordre : l'inventaire d'abord, les
// interventions signalées pendant cet inventaire ensuite (elles portent son inventory_id)
function settlePending(entries, signal)
{
	const result = entries.slice()
	const inventoryIds = {}
	return entries.reduce((chain, entry, index) => chain.then(() => {
		if (entry.pending_inventory)
		{
			const { pending_inventory, ...rest } = entry
			return saveStaffInventory(pending_inventory.payload, signal).then(saved => {
				inventoryIds[entry.id] = saved._id
				result[index] = { ...rest, inventory_id: saved._id }
			})
		}
		if (entry.pending_intervention)
		{
			const { pending_intervention, ...rest } = entry
			const inventoryId = pending_intervention.after_inventory ? inventoryIds[pending_intervention.after_inventory] : null
			const doc = inventoryId ? { ...pending_intervention.doc, inventory_id: inventoryId } : pending_intervention.doc
			return createIntervention(doc, pending_intervention.photos, signal).then(created => {
				result[index] = { ...rest, intervention_id: created._id }
			})
		}
		return null
	}), Promise.resolve()).then(() => result)
}

export function saveTourEntries(previous, next)
{
	const shape = (entry) => JSON.stringify(entry, (key, value) => (key === 'file' ? undefined : value))
	const before = {}
	;(previous.entries || []).forEach(entry => {
		before[entry.id] = shape(entry)
	})
	const nextEntries = next.entries || []
	const upserts = nextEntries.filter(entry => before[entry.id] !== shape(entry))
	const nextIds = new Set(nextEntries.map(entry => entry.id))
	const removes = (previous.entries || []).filter(entry => !nextIds.has(entry.id)).map(entry => entry.id)
	if (upserts.length === 0 && removes.length === 0)
		return Promise.resolve(next)
	// Photos déjà envoyées pendant une tentative précédente : elles gardent leur file_id au lieu d'être renvoyées
	const queue = () => enqueueTourEntries(previous._id, upserts.map(entry => {
		if (entry.pending_inventory)
			return { ...entry, pending_inventory: { payload: { ...entry.pending_inventory.payload, pictures: (entry.pending_inventory.payload.pictures || []).map(picture => {
				const { file, ...rest } = picture
				if (picture.file_id)
					return rest
				return (file && uploadedFiles.has(file)) ? { ...rest, file_id: uploadedFiles.get(file)._id } : picture
			}) } } }
		if (entry.pending_intervention)
			return { ...entry, pending_intervention: { ...entry.pending_intervention, photos: (entry.pending_intervention.photos || []).map(photo => (photo.file && uploadedFiles.has(photo.file) ? { file_id: uploadedFiles.get(photo.file)._id } : (photo.file_id ? { file_id: photo.file_id } : (photo.file ? { file: photo.file } : null)))).filter(Boolean) } }
		if (entry.kind !== 'photo' || !entry.photo || entry.photo.file_id || !entry.photo.file || !uploadedFiles.has(entry.photo.file))
			return entry
		const { file, url, thumb, ...photo } = entry.photo
		return { ...entry, photo: { ...photo, file_id: uploadedFiles.get(file)._id } }
	}), removes).then(() => hydrateTour(next))
	if (isOffline() || hasPending(previous._id))
		return queue()
	const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
	const signal = controller ? controller.signal : undefined
	const photos = upserts.reduce((count, entry) => count + (entry.kind === 'photo' && entry.photo && !entry.photo.file_id && entry.photo.file ? 1 : 0) + (entry.pending_intervention ? (entry.pending_intervention.photos || []).filter(photo => photo.file).length : 0) + (entry.pending_inventory ? (entry.pending_inventory.payload.pictures || []).filter(picture => picture.file && !picture.file_id).length : 0), 0)
	const timer = controller ? setTimeout(() => controller.abort(), SAVE_TIMEOUT_MS + photos * PHOTO_TIMEOUT_MS) : null
	return settlePending(upserts, signal)
		.then(settled => mapLimit(settled, UPLOAD_PARALLEL, entry => {
		if (entry.kind !== 'photo' || !entry.photo || entry.photo.file_id || !entry.photo.file)
			return Promise.resolve(entry)
		const { file, url, thumb, ...photo } = entry.photo
		return uploadOnce(file, 'inventories/', signal).then(saved => ({ ...entry, photo: { ...photo, file_id: saved._id } }))
	}))
		.then(entries => postJson('dashboard/tours/save-entries', { id: previous._id, upserts: entries, removes }, signal))
		.then(tour => hydrateTour(requireDocument(tour, 'tour')))
		.catch(error => {
			if (isNetworkError(error))
				return queue()
			throw error
		})
		.finally(() => clearTimeout(timer))
}

// Réel : liste des emplacements
export function fetchPitches()
{
	return getJson('dashboard/pitches/list')
		.then(pitches => {
			if (!Array.isArray(pitches))
				throw new Error('pitches')
			return pitches
		})
}

// La recherche serveur est une regex : « A(12) » ou « B+3 » la casseraient. On n'envoie donc que le plus
// long morceau de lettres et chiffres de l'emplacement (tous les séjours de l'emplacement le contiennent).
function searchableLocation(location)
{
	const chunks = (location || '').match(/[A-Za-z0-9À-ɏ]+/g) || []
	return chunks.reduce((longest, chunk) => (chunk.length > longest.length ? chunk : longest), '')
}

// Réel : séjours d'un emplacement. « M06 » trouve aussi « M066 » : on ne garde que l'emplacement exact.
// Tous les critères doivent être envoyés, vides sinon.
export function fetchStaysForPitch(location)
{
	const search = searchableLocation(location)
	if (!search)
		return Promise.resolve([])
	return postJson('dashboard/stays/search', {
		client_number: '',
		stay_number: '',
		location: search,
		location_category: '',
		after_arrival: '',
		before_arrival: '',
		after_departure: '',
		before_departure: ''
	})
		.then(stays => {
			if (!Array.isArray(stays))
				throw new Error('stays')
			const needle = normalizeLocation(location)
			return stays.filter(stay => normalizeLocation(stay.location) === needle)
		})
}

// Réel : un séjour par son numéro (null s'il n'existe pas)
export function fetchStayByNumber(stayNumber)
{
	return postJson('dashboard/stays/find', { stay_number: Number(stayNumber) })
		.then(stay => {
			if (stay === false)
				throw new Error('stay')
			if (!stay || stay.status === 'canceled')
				return null
			return stay
		})
}

// Réel : vacanciers d'une liste de séjours, indexés par numéro de client
export function fetchClients(stays)
{
	const numbers = Array.from(new Set((stays || []).map(stay => stay.client_number).filter(Boolean)))
	if (numbers.length === 0)
		return Promise.resolve({})
	return postJson('dashboard/clients/find-many', { clients: numbers })
		.then(clients => {
			const byNumber = {}
			if (Array.isArray(clients))
				clients.forEach(client => {
					byNumber[client.client_number] = client
				})
			return byNumber
		})
		.catch(() => ({}))
}

// Réel : inventaires faits par le vacancier pour un séjour (source client, ou absente sur les anciens)
export function fetchGuestInventories(stayNumber)
{
	return getJson('dashboard/inventories/find-by-stay-number?stay_number=' + encodeURIComponent(stayNumber))
		.then(inventories => (Array.isArray(inventories) ? inventories.filter(inventory => !!inventory.create_user && inventory.source !== 'staff') : []))
		.catch(() => [])
}

// Réel : modèles d'inventaire par hébergement (« Mes hébergements »)
export function fetchAccommodations()
{
	return getJson('dashboard/accommodations/list')
		.then(accommodations => {
			if (!Array.isArray(accommodations))
				throw new Error('accommodations')
			return accommodations
		})
}

// Réel : dernier inventaire en date fait sur l'emplacement, toutes sources confondues (décision 40) :
// null s'il n'y en a aucun (ou sans réseau). Sans les photos ni les commentaires.
export function fetchLastPitchInventory(location)
{
	return getJson('dashboard/inventories/last-by-location?location=' + encodeURIComponent(location))
		.then(inventory => {
			if (inventory === false)
				throw new Error('reference')
			return inventory
		})
		// Sans réseau (logement mal couvert) ni copie du service worker : pas de référence, l'inventaire se compare au modèle
		// plutôt que de rester bloqué sur « Impossible de charger »
		.catch(error => {
			if (isNetworkError(error))
				return null
			throw error
		})
}

// Inventaire du tour mis de côté (« Enregistrer pour plus tard »), voir fieldDraft.js. Réseau nécessaire : le brouillon
// est sur le serveur, il se reprend depuis n'importe quel téléphone.
export function fetchInventoryDraft(tourId, location)
{
	return getJson('dashboard/inventories/draft?tour_id=' + encodeURIComponent(tourId) + '&location=' + encodeURIComponent(location), FRESH)
		.then(draft => {
			if (draft === false)
				throw new Error('draft')
			return draft
		})
}

// Emplacements d'un tour qui ont un brouillon : [{ location, update_user, update_time }]
export function fetchInventoryDrafts(tourId)
{
	return getJson('dashboard/inventories/drafts?tour_id=' + encodeURIComponent(tourId), FRESH)
		.then(list => {
			if (!Array.isArray(list))
				throw new Error('drafts')
			return list
		})
}

// Les photos du brouillon (commentaires et problèmes signalés compris) partent d'abord : le brouillon n'en garde que le file_id
export function uploadDraftFiles(notes, interventions)
{
	if (isOffline())
		return Promise.reject(new Error('offline'))
	return mapLimit(notes.photos, UPLOAD_PARALLEL, photo => {
		if (photo.file_id || !photo.file)
			return Promise.resolve(photo)
		return uploadOnce(photo.file, 'inventories/').then(saved => ({ ...photo, file_id: saved._id }))
	})
		.then(photos => mapLimit(interventions, 1, item => mapLimit(item.photos || [], UPLOAD_PARALLEL, photo => {
			if (photo.file_id || !photo.file)
				return Promise.resolve(photo)
			return uploadOnce(photo.file, 'interventions/').then(saved => ({ file_id: saved._id }))
		}).then(uploaded => ({ ...item, photos: uploaded })))
			.then(uploadedInterventions => ({ notes: { ...notes, photos }, interventions: uploadedInterventions })))
}

export function saveInventoryDraft(tourId, location, user, state)
{
	if (isOffline())
		return Promise.reject(new Error('offline'))
	return postJson('dashboard/inventories/save-draft', { tour_id: tourId, location, user: user ? user._id : null, state })
		.then(draft => requireDocument(draft, 'draft'))
}

// SIMULATION : inventaire staff déjà fait pour ce séjour et ce type ?
// Le serveur n'a pas encore de route pour le savoir : toujours non (un nouvel envoi pour le même séjour
// et le même type modifie l'inventaire du personnel existant, il ne crée pas de doublon).
export function isStaffInventoryDone(stay, type)
{
	return false
}

// Réel : versions remplacées d'un inventaire du personnel, de la plus récente à la plus ancienne
export function fetchInventoryVersions(inventoryId)
{
	return getJson('dashboard/inventories/versions?id=' + encodeURIComponent(inventoryId))
		.then(versions => {
			if (!Array.isArray(versions))
				throw new Error('versions')
			return versions
		})
}

// Réel : enregistrer un inventaire du personnel. Les photos partent d'abord sur /dashboard/files/add-image,
// puis le document est envoyé avec leurs file_id. Un échec (réseau, photo, serveur) rejette : l'écran garde
// toute la saisie. Renvoie l'inventaire enregistré ({ _id, version, ... }).
export function saveStaffInventory(document, signal)
{
	if (isOffline())
		return Promise.reject(new Error('offline'))
	return mapLimit(document.pictures || [], UPLOAD_PARALLEL, picture => {
		const { file, ...rest } = picture
		if (rest.file_id || !file)
			return Promise.resolve(rest)
		return uploadOnce(file, 'inventories/', signal).then(saved => ({ ...rest, file_id: saved._id }))
	})
		.then(pictures => postJson('dashboard/inventories/insert-staff', { ...document, pictures }, signal))
		.then(saved => requireDocument(saved, 'save'))
}

// Réel : ajout, modification ou suppression d'une contribution (commentaire, photos) sur un inventaire
// déjà enregistré (décisions 14, 15 et 31). user : la personne qui agit, seule autorisée à modifier ou
// supprimer sa contribution.
export function saveContribution(inventoryId, action, payload, user)
{
	if (isOffline())
		return Promise.reject(new Error('offline'))
	const body = { id: inventoryId, action, user: user ? user._id : null }
	let ready = Promise.resolve(body)
	if (action === 'add-comment')
		ready = Promise.resolve({ ...body, comment: commentToServer(payload.comment) })
	else if (action === 'update-comment')
		ready = Promise.resolve({ ...body, comment: { id: payload.comment.id, comment: payload.comment.comment, commentDeletedTime: payload.comment.commentDeletedTime || null } })
	else if (action === 'remove-photos')
		ready = Promise.resolve({ ...body, ids: payload.ids })
	else if (action === 'add-photos')
		ready = mapLimit(payload.photos, UPLOAD_PARALLEL, photo => uploadOnce(photo.file, 'inventories/').then(saved => ({ id: photo.id, file_id: saved._id, description: photo.description, user: photo.user, timestamp: photo.timestamp })))
			.then(photos => ({ ...body, photos }))
	return ready
		.then(request => postJson('dashboard/inventories/contribute', request))
		.then(saved => requireDocument(saved, 'contribution'))
}

function commentToServer(comment)
{
	return { id: comment.id, comment: comment.comment, user: comment.commentUser, timestamp: comment.commentTime, theme: null, after: !!comment.after }
}

// SIMULATION : réglage « Nous faisons des réassorts en saison » (décision 37), en attendant la clé
// de configuration côté serveur. Oui par défaut ; ?demo=no-restock pour le parcours sans réassort.
export function restockInSeason()
{
	return !demoFlags().includes('no-restock')
}

// SIMULATION : réglage « Tour avec inventaire » des paramètres généraux (décision 50), en attendant
// la clé de configuration côté serveur. Avec inventaire par défaut ; ?demo=inspection pour l'inspection seule.
export function tourWithInventory()
{
	return !demoFlags().includes('inspection')
}

// Vue Plan ou Liste du tour, mémorisée sur ce téléphone (décision 50)
const TOUR_VIEW_KEY = 'field_inventories_tour_view'

export function getTourView()
{
	try
	{
		return localStorage.getItem(TOUR_VIEW_KEY) === 'list' ? 'list' : 'plan'
	}
	catch (e)
	{
		return 'plan'
	}
}

export function setTourView(view)
{
	try
	{
		localStorage.setItem(TOUR_VIEW_KEY, view)
	}
	catch (e)
	{
		return ;
	}
}

// Réel : membres de l'équipe, pour « Attribuer à » (même route que le module Interventions)
let usersCache = null

export function fetchStaffUsers()
{
	if (usersCache)
		return Promise.resolve(usersCache)
	return getJson('dashboard/users/list')
		.then(users => {
			usersCache = Array.isArray(users) ? users : []
			return usersCache
		})
		.catch(() => [])
}

// Réel : créer une intervention (dashboard/interventions/create). doc porte inventory_id (l'inventaire
// enregistré) et flag (« restocking » pour un réassort, « repair » pour une réparation). Les photos
// partent d'abord sur /dashboard/files/add-image, leurs adresses vont dans photo_urls.
export function createIntervention(doc, photos, signal)
{
	if (isOffline())
		return Promise.reject(new Error('offline'))
	// Une photo déjà sur le serveur (photo du tour : file_id) est jointe telle quelle ; une photo prise sur le
	// téléphone (file) est envoyée d'abord. Les autres (aperçus sans fichier) sont ignorées.
	return mapLimit(photos || [], UPLOAD_PARALLEL, photo => {
		if (photo.file_id)
			return Promise.resolve(photoUrl(photo.file_id))
		if (photo.file)
			return uploadOnce(photo.file, 'interventions/', signal).then(saved => photoUrl(saved._id))
		return Promise.resolve(null)
	})
		.then(urls => postJson('dashboard/interventions/create', { ...doc, photo_urls: (doc.photo_urls || []).concat(urls.filter((url, index) => !!url && urls.indexOf(url) === index)) }, signal))
		.then(created => requireDocument(created, 'intervention'))
}

// SIMULATION : journal de Mon activité (canvas A1 à A5). En attendant une route serveur qui renvoie
// les actions d'une personne, tous types confondus, le journal est gardé sur ce téléphone.
const ACTIVITY_KEY = 'field_inventories_activity'
const ACTIVITY_MAX = 300

export function fetchActivity()
{
	try
	{
		const list = JSON.parse(localStorage.getItem(ACTIVITY_KEY) || '[]')
		return Promise.resolve(Array.isArray(list) ? list : [])
	}
	catch (e)
	{
		return Promise.resolve([])
	}
}

export function recordActivity(entry)
{
	return fetchActivity().then(list => {
		try
		{
			localStorage.setItem(ACTIVITY_KEY, JSON.stringify([entry].concat(list).slice(0, ACTIVITY_MAX)))
		}
		catch (e)
		{
			return entry
		}
		return entry
	})
}

export function removeActivity(id)
{
	return fetchActivity().then(list => {
		try
		{
			localStorage.setItem(ACTIVITY_KEY, JSON.stringify(list.filter(entry => entry.id !== id)))
		}
		catch (e)
		{
			return null
		}
		return null
	})
}

// Derniers emplacements utilisés, gardés sur ce téléphone (décision 17)
const RECENT_KEY = 'field_inventories_recent_pitches'
const RECENT_MAX = 5

export function getRecentLocations()
{
	try
	{
		const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')
		return Array.isArray(list) ? list : []
	}
	catch (e)
	{
		return []
	}
}

export function addRecentLocation(location)
{
	try
	{
		const list = getRecentLocations().filter(item => normalizeLocation(item) !== normalizeLocation(location))
		list.unshift(location)
		localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)))
	}
	catch (e)
	{
		return ;
	}
}
